import { apiBaseUrl } from '../lib/env.js';
import { useSession } from '../state/session.js';
import { IS_DEMO } from '../demo/config.js';
import { mockRequest } from '../demo/api.js';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  auth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path, apiBaseUrl);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function raw<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = opts.auth === false ? null : useSession.getState().accessToken;
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const message = (json as { error?: { message?: string } }).error?.message ?? res.statusText;
    const code = (json as { error?: { code?: string } }).error?.code ?? 'unknown';
    throw new ApiError(res.status, code, message);
  }
  return (json as { data: T }).data;
}

async function tryRefresh(): Promise<boolean> {
  const { refreshToken, setTokens, clear } = useSession.getState();
  if (!refreshToken) return false;
  try {
    const tokens = await raw<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      auth: false,
    });
    setTokens(tokens);
    return true;
  } catch {
    clear();
    return false;
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (IS_DEMO) {
    try {
      return await mockRequest<T>(path, opts);
    } catch (err) {
      throw new ApiError(400, 'demo_error', err instanceof Error ? err.message : 'Demo error.');
    }
  }
  try {
    return await raw<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && opts.auth !== false) {
      const ok = await tryRefresh();
      if (ok) return raw<T>(path, opts);
    }
    throw err;
  }
}
